import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@root/hooks/useQuery';

import AWSIcon from '@root/assets/img/header/cloud_providers/aws.png';
import AzureIcon from '@root/assets/img/header/cloud_providers/azure.png';
import GCPIcon from '@root/assets/img/header/cloud_providers/gcp.png';
import {
  UPDATE_ALL_RESOURCES_PAGE,
  UPDATE_CRITICAL_RESOURCES_PAGE,
  UPDATE_KEY_INSIGHTS_PAGE,
  UPDATE_REGIONS,
  UPDATE_RESOURCE_LIST,
} from '@root/constants/actionTypes';
import { INVENTORY_BASE_PATH, resolveInventoryTab } from '@root/constants/inventoryTabs';
import { IDENTITY_TABS, THREAT_TABS, resolveThreatEventsTab, resolveThreatTab } from '@root/constants/moduleTabs';
import { useEnvironmentTags, useEnvironments, useSelectedEnvironments } from '@root/hooks/environments';
import useTimeFilter from '@root/hooks/SelectTimeFilter.jsx';
import { useQuery as useQueryReact } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import EnvironmentDropdown from './EnvionmentDropdown';
import ScopeDropdown from './ScopeDropdown';
import './style.scss';
import TimeFilterDropdown from './TimeFilterDropdown';
import agent from '@root/agent';
import moment from 'moment';
import { useCloudProvider } from '../../content/CloudProviderBar/CloudProviderContext';
// import TimeRangeSelector from './TimeRange';
// import { setTimeFilter } from '@root/containers/global_actions';

function HeaderContent() {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const hasExecuted = useRef(false);
  // const history = useHistory();
  // const fromDSOverview = query.get('fromDSOverview');
  const fromAccount = query.get('account');
  // const fromScanHistory = query.get('from');
  const notification_key = query.get('notification_key');
  // const fromThreatSeverity = query.get('fromThreatSeverity');
  const environmentTags = useEnvironmentTags();
  const environments = useEnvironments();
  const fromScanToEvent = query.get('fromScanReport');
  const fromLinkCopySideOverlay = query.get('fromLinkCopySideOverlay');
  // const fromDefaultTimeRange = query.get('fromDefaultTimeRange');
  // const threatSeverityTimeRange = query.get('threatTimeRange');
  // const defaultTimeRange = query.get('defaultTimeRange');
  // const fromThreatAnomalyGraph = query.get('threat_event_action');
  // commenting out this fromRuntimeFilter because it is not used anywehere except this file
  // const fromRuntimeTimeFilter = query.get('fromRuntimeTimeFilter');
  // const fromEventsOverview = query.get('fromEventsOverview');
  // const fromSideoverlay = query.get('fromSideoverlay');
  const { selectedEnvironmentTags, setSelectedEnvironmentTags, setSelectedEnvironments } = useSelectedEnvironments();
  const { handleChangeCloudProvider } = useCloudProvider();
  // const eventsOverviewTimeRange = query.get('eventsOverviewTimeRange');

  const { timeFilter, updateTimeFilter, timeRange, rangeValue, updateRangeValue } = useTimeFilter();

  // const [, updateRangeValue] = useState('15m');
  const [selectedEnvDropdown, setSelectedEnvDropdown] = useState([]);
  const allEnvironments = useEnvironments();
  // const [value, setValue] = useState('15m');
  // console.log();
  // console.log(allEnvironments, 'jfn');

  const { data: complianceData } = useQueryReact(
    ['get_compliance_account', allEnvironments.map(e => e.id)],
    () =>
      agent.Compliance.get_compliance_account({
        environments: allEnvironments.map(env => env.id),
      }),
    { enabled: allEnvironments.map(env => env.id)?.length > 0 && location.pathname === '/compliance/breakdown' },
  );
  // console.log(complianceData, 'dvndfss');
  const threatTab = useMemo(() => {
    if (location.pathname === THREAT_TABS.basePath) return resolveThreatTab(query.toString());
    return null;
  }, [location.pathname, query]);

  // Overview and the event tables are nested tabs of the Threat module's Events tab now, so the
  // range presets that used to key off the `/events/*` routes key off that nested tab instead.
  const threatEventsTab = useMemo(
    () => (threatTab === 'events' ? resolveThreatEventsTab(query.toString()) : null),
    [threatTab, query],
  );

  useEffect(() => {
    const isThreatEventsTable = location.pathname === '/events/all' || (threatEventsTab && threatEventsTab !== 'Overview');
    if (isThreatEventsTable && (notification_key || fromLinkCopySideOverlay)) {
      updateRangeValue('90d');
    }
  }, [location.pathname, threatEventsTab, fromLinkCopySideOverlay, notification_key, updateRangeValue]);
  const timeDifference = useMemo(() => {
    const startDate = moment(timeRange.from, 'YYYY/MM/DD HH:mm:ss');
    const endDate = moment(timeRange.to, 'YYYY/MM/DD HH:mm:ss');

    // Calculate differences
    const diffInMinutes = endDate.diff(startDate, 'minutes');
    const diffInHours = endDate.diff(startDate, 'hours');
    const diffInDays = endDate.diff(startDate, 'days');

    // Determine the most appropriate unit
    if (diffInDays >= 1) {
      return `${diffInDays}d`;
    } else if (diffInHours >= 1) {
      return `${diffInHours}h`;
    } else {
      return `${diffInMinutes}m`;
    }
  }, [timeRange]);

  useEffect(() => {
    const isThreatEventsOverview = location.pathname === '/events/overview' || threatEventsTab === 'Overview';
    if (isThreatEventsOverview && !hasExecuted.current && timeDifference === '1d') {
      updateRangeValue(timeDifference);
      hasExecuted.current = true;
    }
  }, [location.pathname, threatEventsTab, timeDifference, updateRangeValue]);

  // useEffect(() => {
  //   const unlist = history.listen(location => {
  //     setNavStack(prev => {
  //       if (prev.length >= 4) {
  //         prev.splice(0, 1);
  //       }

  //       if (prev.includes(location.pathname)) {
  //         const newStack = prev.filter(path => path !== location.pathname);
  //         newStack.push(location.pathname);
  //         return newStack;
  //       }

  //       return [...prev, location.pathname];
  //     });
  //   });

  //   return unlist;
  // }, [history]);

  const setSelectedEnvironmentsRef = useRef(setSelectedEnvironments);
  setSelectedEnvironmentsRef.current = setSelectedEnvironments;

  const [disabledEnvironmentOptionValues, setDisabledEnvironmentOptionValues] = useState([]);

  const environmentTagOptions = useMemo(() => {
    return environmentTags.map(tag => ({ label: tag, value: tag }));
  }, [environmentTags]);

  // Both Threat module tabs (Threat Story, Events) are driven by the global range.
  const isThreatModulePage = location.pathname === THREAT_TABS.basePath;

  // Identity: only the Overview tab has cards the global Time Range can reach. Non-Human
  // Identity drives its own range per card, and the Human Identity tabs and the Overview
  // drill-down table are backed by endpoints that take no date parameter at all, so the
  // dropdown is hidden on them.
  const isIdentityTabWithoutTimeFilter = useMemo(
    () =>
      location.pathname === '/identity/overview/table' ||
      (location.pathname === IDENTITY_TABS.basePath &&
        ['human-identity', 'non-human-identity'].includes(IDENTITY_TABS.resolve(location.search))),
    [location.pathname, location.search],
  );

  const timeFilterOptions = useMemo(() => {
    if (
      location.pathname === '/events/all' ||
      location.pathname === '/events/suppressed' ||
      location.pathname === '/kubernetes-live/events' ||
      location.pathname === '/events/overview' ||
      isThreatModulePage
    )
      return [
        { label: 'Last 15m', value: '15m' },
        { label: 'Last 30m', value: '30m' },
        { label: 'Last 1h', value: '1h' },
        { label: 'Last 6h', value: '6h' },
        { label: 'Last 12h', value: '12h' },
        { label: 'Last 24h', value: '1d' },
        { label: 'Last 2d', value: '2d' },
        { label: 'Last 7d', value: '7d' },
        { label: 'Last 30d', value: '30d' },
        { label: 'Last 90d', value: '90d' },
      ];

    return [
      { label: '24h', value: '24h' },
      { label: '7d', value: '7d' },
      { label: '30d', value: '30d' },
      { label: '90d', value: '90d' },
    ];
  }, [location.pathname, isThreatModulePage]);

  const environmentOptions = useMemo(() => {
    const awsEnvironments = [];
    const gcpEnvironments = [];
    const azureEnvironments = [];
    let _environments = allEnvironments;

    const complianceName = query.get('compliance');

    if (location.pathname === '/compliance/breakdown') {
      if (complianceData) {
        const filteredCompliance = complianceData?.data?.filter(compliance =>
          Object.keys(compliance)?.includes(complianceName),
        );
        // console.log(filteredCompliance, filteredCompliance[0][complianceName], 'fhjhdfss');
        if (filteredCompliance?.length > 0) {
          _environments = _environments.filter(env => filteredCompliance[0][complianceName]?.includes(env.id));
        }
      }
    }

    for (const env of _environments) {
      if (env.cloudProvider === 'aws') {
        awsEnvironments.push({ label: env.id, value: env.id, data: env });
      }

      if (env.cloudProvider === 'gcp') {
        gcpEnvironments.push({ label: env.id, value: env.id, data: env });
      }

      if (env.cloudProvider === 'azure') {
        azureEnvironments.push({ label: env.id, value: env.id, data: env });
      }
    }

    const groups = [];

    if (awsEnvironments.length) {
      groups.push({
        label: 'AWS',
        options: awsEnvironments,
        icon: AWSIcon,
      });
    }

    if (gcpEnvironments.length) {
      groups.push({
        label: 'GCP',
        options: gcpEnvironments,
        icon: GCPIcon,
      });
    }

    if (azureEnvironments.length) {
      groups.push({
        label: 'Azure',
        options: azureEnvironments,
        icon: AzureIcon,
      });
    }

    return groups;
  }, [complianceData, allEnvironments, location.pathname, query]);

  const handleEnvironmentTagsChange = useCallback(
    selectedTags => {
      const prev = selectedEnvironmentTags;

      if (!prev.includes('Global') && selectedTags.includes('Global')) {
        // Global tag is being selected. We unselect all others.
        // Also, all environments are selectable when Global tag is selected.
        setDisabledEnvironmentOptionValues([]);
        setSelectedEnvironmentTags(['Global']);
      } else {
        if (selectedTags.length) {
          let finalSelectedTags;

          if (prev.includes('Global')) {
            // Unselect Global when other tags are selected.
            finalSelectedTags = selectedTags.filter(t => t !== 'Global');
          } else {
            finalSelectedTags = selectedTags;
          }

          // START: Disable environment options for unselected tags.
          const disabledEnvValues = [];
          const selectedEnvironmentTagsMap = {};
          for (const t of finalSelectedTags) {
            selectedEnvironmentTagsMap[t] = true;
          }
          for (const env of environments) {
            if (!env.tags.find(t => selectedEnvironmentTagsMap[t])) {
              disabledEnvValues.push(env.id);
            }
          }
          setDisabledEnvironmentOptionValues(disabledEnvValues);
          // END: Disable environment options for unselected tags.

          // START: Pre-select environment options which have the newly selected tags
          const newSelectedTags = [];
          const unselectedTags = [];

          for (const t of selectedTags) {
            if (!prev.includes(t)) {
              newSelectedTags.push(t);
            }
          }

          for (const t of prev) {
            if (!selectedTags.includes(t)) {
              unselectedTags.push(t);
            }
          }

          const newSelectedTagsMap = {};

          for (const t of newSelectedTags) {
            newSelectedTagsMap[t] = true;
          }

          if (newSelectedTags.length || unselectedTags.length) {
            setSelectedEnvironments(prevEnvs => {
              const newEnvsToBeSelected = [];
              const envsToBeUnselected = {};

              for (const e of environments) {
                if (!e.tags.find(t => finalSelectedTags.includes(t))) {
                  // We unselect the previously selected environments which are no longer
                  // linked to the newly updated selected tags.
                  envsToBeUnselected[e.id] = true;
                }

                if (disabledEnvValues.find(id => e.id === id)) {
                  envsToBeUnselected[e.id] = true;
                }

                if (!envsToBeUnselected[e.id] && e.tags.find(t => newSelectedTagsMap[t])) {
                  // If this environment's tags are in the newly selected tags
                  // add the environment to the selection.
                  newEnvsToBeSelected.push(e);
                }
              }

              const selected = [...prevEnvs, ...newEnvsToBeSelected]
                .reduce((acc, curr) => (acc.find(e => e.id === curr.id) ? acc : [...acc, curr]), []) // Remove duplicates
                .filter(env => !envsToBeUnselected[env.id]);

              return selected;
            });
          }
          // END: Pre-select environment options which have the newly selected tags

          setSelectedEnvironmentTags(finalSelectedTags);
        } else {
          setSelectedEnvironmentTags(['Global']);
          setSelectedEnvironments(environments);
          setDisabledEnvironmentOptionValues([]);
        }
      }
    },
    [selectedEnvironmentTags, setSelectedEnvironmentTags, environments, setSelectedEnvironments],
  );

  // Handle Global tag being selected. We pre-selected all environments.
  useEffect(() => {
    if (selectedEnvironmentTags.includes('Global')) {
      setSelectedEnvironmentsRef.current(environments.map(e => e));
    }
    if (fromAccount) {
      setSelectedEnvironmentsRef.current(environments.filter(e => e.id === fromAccount));
    }
  }, [selectedEnvironmentTags, environments, fromAccount]);

  const updateResourceOverview = useCallback(() => {
    dispatch({
      type: UPDATE_REGIONS,
    });
  }, [dispatch]);

  const updateResourceEvaluation = useCallback(() => {
    dispatch({
      type: UPDATE_RESOURCE_LIST,
    });
  }, [dispatch]);

  const updateResources = useCallback(() => {
    dispatch({
      type: UPDATE_ALL_RESOURCES_PAGE,
    });
  }, [dispatch]);

  const updateResourcesCriticalTab = useCallback(() => {
    dispatch({
      type: UPDATE_CRITICAL_RESOURCES_PAGE,
    });
  }, [dispatch]);
  const updateResourcesKeyInsightTab = useCallback(() => {
    dispatch({
      type: UPDATE_KEY_INSIGHTS_PAGE,
    });
  }, [dispatch]);
  const handleEnvironmentsChange = useCallback(
    selectedEnvIds => {
      if (selectedEnvIds.length !== 0) {
        let value = environments.filter(e => selectedEnvIds.includes(e.id));
        const isAWSPresent = value.some(environment => environment.cloudProvider === 'aws');
        const isGCPPresent = value.some(environment => environment.cloudProvider === 'gcp');
        const isAZUREPresent = value.some(environment => environment.cloudProvider === 'azure');
        setSelectedEnvironments(value);
        setSelectedEnvDropdown(value);
        if (isAWSPresent && isGCPPresent && isAZUREPresent) {
          handleChangeCloudProvider('all');
        } else if (isAWSPresent) {
          handleChangeCloudProvider('aws');
        } else if (isGCPPresent) {
          handleChangeCloudProvider('gcp');
        } else {
          handleChangeCloudProvider('azure');
        }
      } else {
        handleChangeCloudProvider('all');
        setSelectedEnvDropdown([]);
        setSelectedEnvironments(environments);
      }

      updateResourceOverview();
      updateResourceEvaluation();
      updateResources();
      updateResourcesCriticalTab();
      updateResourcesKeyInsightTab();
    },

    [
      environments,
      handleChangeCloudProvider,
      setSelectedEnvironments,
      updateResourceEvaluation,
      updateResourceOverview,
      updateResources,
      updateResourcesCriticalTab,
      updateResourcesKeyInsightTab,
    ],
  );

  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0px' }}
      className="HeaderContent__Container-main">
      {location.pathname !== '/scan-history/scan-report' &&
        // location.pathname !== '/rules' &&
        location.pathname !== '/scan-history/scanner' &&
        location.pathname !== '/environments/connect' &&
        location.pathname !== '/onboarding' &&
        location.pathname !== '/select_compliance' &&
        location.pathname !== '/rbac' &&
        location.pathname !== '/rules/table' &&
        location.pathname !== '/remediation' &&
        location.pathname !== '/risk/risk-table/remediation' &&
        location.pathname !== '/notifications/jira' &&
        location.pathname !== '/code/repository-scanning' &&
        !fromScanToEvent && (
          <>
            <ScopeDropdown
              options={environmentTagOptions}
              value={selectedEnvironmentTags}
              onChange={handleEnvironmentTagsChange}
            />
            {/* <Select
              placeholder={!selectedEnvironmentTags.length ? 'Global' : 'Scope'}
              options={environmentTagOptions}
              value={selectedEnvironmentTags}
              onChange={handleEnvironmentTagsChange}
              transparentBg
            /> */}
            <EnvironmentDropdown
              allOptions={environmentOptions}
              disabledOptions={disabledEnvironmentOptionValues}
              value={selectedEnvDropdown?.map(e => e.id)}
              onChange={handleEnvironmentsChange}
              singleSelect
            />

            {/* <SelectGrouped
              placeholder={!selectedEnvironments.length ? 'All Environments' : 'Environments'}
              selectAllTitle="All Environments"
              allSelectedTitle="All Environments"
              optionGroups={environmentOptions}
              disabledOptionValues={disabledEnvironmentOptionValues}
              value={selectedEnvironments.map(e => e.id)}
              enableSelectAll
              transparentBg
              onChange={handleEnvironmentsChange}
            /> */}
            {!isIdentityTabWithoutTimeFilter &&
              (location.pathname.includes('identity') ||
                location.pathname === '/risk' ||
                location.pathname === '/dashboard' ||
                location.pathname === '/events' ||
                location.pathname === '/remediation-report' ||
                location.pathname === '/remediation-hub' ||
                location.pathname === '/compliance' ||
                // Inventory: only the Overview and Key Insights tabs take a time filter.
                (location.pathname === INVENTORY_BASE_PATH &&
                  ['overview', 'key-insight'].includes(resolveInventoryTab(location.search))) ||
                location.pathname === '/compliance/breakdown' ||
                location.pathname === '/identity' ||
                location.pathname === '/scan-history' ||
                location.pathname === '/security-score' ||
                location.pathname === '/kubernetes-live/overview' ||
                location.pathname === '/kubernetes-live/compliance' ||
                location.pathname === '/kubernetes-live/runtime' ||
                location.pathname === '/kubernetes-live/suppressed' ||
                // location.pathname === '/kubernetes-live/inventory' ||
                location.pathname === '/kubernetes-live/vulnerabilities') && (
                <>
                  <TimeFilterDropdown
                    setValue={updateTimeFilter}
                    value={timeFilter}
                    options={timeFilterOptions}
                    singleSelect
                  />
                  {/* <TimeRangeSelector /> */}
                </>
              )}
            {(location.pathname === '/events/all' ||
              location.pathname === '/events/suppressed' ||
              location.pathname === '/kubernetes-live/events' ||
              location.pathname === '/events/overview' ||
              isThreatModulePage) && (
              <TimeFilterDropdown
                setValue={option => updateRangeValue(option)}
                value={rangeValue}
                options={timeFilterOptions}
                singleSelect
              />
            )}
            {/* <CSPMQueryDashboard /> */}
          </>
        )}
    </div>
  );
}

export default HeaderContent;
